<script>
import { sortBy } from 'lodash-es';
import constants from '@/js/constants';
import CustomImage from '@/components/card/CustomImage';
import LoadingIcon from '@/components/v3/base/LoadingIcon2';
import RejectedReasonBox from '@/components/card/RejectedReasonBox';
import Checkbox from '@/components/v3/base/Checkbox';
import Chip from '@/components/v3/base/Chip';
import IconButton from '@/components/v3/base/IconButton';
import BiasAvatar1 from '@/assets/icons/candidate-photo-1.svg';
import BiasAvatar2 from '@/assets/icons/candidate-photo-2.svg';
import BiasAvatar3 from '@/assets/icons/candidate-photo-3.svg';
import BiasAvatar4 from '@/assets/icons/candidate-photo-4.svg';
import BiasAvatar5 from '@/assets/icons/candidate-photo-5.svg';
import DefaultAvatar from '@/assets/img/empty_avatar.png';
import LinkedInIcon from '@/assets/icons/linkedin-icon.svg';
import MailIcon from '@/assets/icons/mail-purple.svg';
import SwitchIcon from '@/assets/icons/switch-purple.svg';
import KebabIcon from '@/assets/icons/kebab-purple.svg';
import Typography from '@/components/v3/base/Typography';

const OVERFLOW_CHIP_WIDTH = 60;
const KEYWORD_CHIP_GAP_WIDTH = 8;
const BIAS_AVATARS = [
  BiasAvatar1,
  BiasAvatar2,
  BiasAvatar3,
  BiasAvatar4,
  BiasAvatar5,
];

export default {
  components: {
    CustomImage,
    LoadingIcon,
    RejectedReasonBox,
    Checkbox,
    Chip,
    IconButton,
    Typography,
  },

  props: {
    candidate: {
      type: Object,
      default: () => ({}),
    },

    projectId: {
      type: String,
      default: '',
    },

    tab: {
      type: String,
      default: '',
    },

    isSelected: {
      type: Boolean,
      default: false,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    grayOutStyle: {
      type: Boolean,
      default: true,
    },

    tabs: {
      type: Array,
      default: () => [],
    },

    disabledMoveCandidate: {
      type: Boolean,
      default: false,
    },

    notInterestedReason: {
      type: String,
    },

    showTooltip: Boolean,
    isHideMenu: Boolean,
  },

  data() {
    return {
      hasKeywordOverflow: false,
      loading: false,
      maxKeywords: 0,
      LinkedInIcon,
      MailIcon,
      SwitchIcon,
      KebabIcon,
    };
  },

  computed: {
    currentProjectSettings() {
      if (!this.projectId) {
        return {};
      }
      return this.$store.getters['projects/settings/getSettingsByProjectId'](
        this.projectId
      );
    },

    isBiasReducerEnabled() {
      return this.currentProjectSettings.bias_reducer;
    },

    linkedinProfile() {
      const linkedinUrlInLinkedAccounts =
        this.candidate?.linked_accounts?.linkedin?.[0];
      const linkedinUrl =
        this.candidate?.linkedin_profile_url ?? linkedinUrlInLinkedAccounts;
      const userEmail = localStorage.getItem('email') ?? '';
      return userEmail && constants.RECRUITER_PROFILE_USERS.includes(userEmail)
        ? this.candidate?.linkedin_recruiter_url
        : linkedinUrl;
    },

    candidateName() {
      if (!this.candidate) return '';
      let name = '';
      if (this.candidate.name) {
        name = this.candidate.name;
      } else {
        name = `${this.candidate.first_name} ${this.candidate.last_name}`;
      }
      return this.getInitialsName(name);
    },

    avatarUrl() {
      if (this.isBiasReducerEnabled) return BIAS_AVATARS[0];
      if (this.loading) return DefaultAvatar;
      if (this.candidate) {
        return (
          this.candidate.picture_url ||
          this.candidate.profile_pic_url ||
          DefaultAvatar
        );
      }
      return DefaultAvatar;
    },

    candidateLocation() {
      if (this.candidate.location) return this.candidate.location;
      let location = '';
      if (this.candidate.city) location += `${this.candidate.city}, `;
      if (this.candidate.state) location += this.candidate.state;
      else location += this.candidate.country;
      return location;
    },

    candidateCurrentTitle() {
      if (!this.candidate.experiences || !this.candidate.experiences.length)
        return '';
      return `${this.candidate.experiences[0].title} at ${this.candidate.experiences[0].company}`;
    },

    candidateExperiencesToDisplay() {
      // return maximum of first 3 experiences
      return this.candidate.experiences.slice(0, 3);
    },

    sortedKeywords() {
      return sortBy(this.candidate.matched_keywords, 'length');
    },

    overflowKeywordsCount() {
      return `+${this.candidate.matched_keywords.length - this.maxKeywords}`;
    },

    filteredTabs() {
      if (!Array.isArray(this.tabs)) return [];

      return this.tabs.filter(
        (t) => t.key !== 'review' && t.key !== this.tab.toLowerCase()
      );
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (
        this.$refs.keywordContainer &&
        this.$refs.keywordContainer.scrollWidth >
          this.$refs.keywordContainer.clientWidth
      ) {
        this.hasKeywordOverflow = true;
        this.maxKeywords = this.getMaxKeywords();
      }
    });
  },

  methods: {
    getInitialsName(name) {
      if (!this.isBiasReducerEnabled) return name;
      if (!name) return '';
      const names = name.split(' ');
      const specialChars = ['.', '-', '_', '('];
      return `${names
        .map((n) => (!specialChars.includes(n.charAt(0)) ? n.charAt(0) : ''))
        .filter((item) => item !== '')
        .join('.')}.`;
    },

    detailed_contacted_status() {
      if (this.tab === 'CONTACTED') {
        if (this.candidate.follow_up_3_date) {
          return `Follow-Up #3 sent: ${this.candidate.follow_up_3_date}`;
        }
        if (this.candidate.follow_up_2_date) {
          return `Follow-Up #2 sent: ${this.candidate.follow_up_2_date}`;
        }
        if (this.candidate.follow_up_1_date) {
          return `Follow-Up #1 sent: ${this.candidate.follow_up_1_date}`;
        }
        return `Initial outreach sent: ${this.candidate.contacted_date}`;
      }
      return '';
    },

    onSelected(val) {
      this.$emit('select');
    },

    openCandidateCard() {
      this.$emit('openCandidateCard', this.candidate);
    },

    select(val) {
      if (this.isSelected === val || this.candidate.bulk_sending_email) return;
      this.onSelected(val);
    },

    onMailClick() {
      this.$emit('mail');
    },

    getMaxKeywords() {
      const keywordChipContainerWidth =
        (this.$refs.keywordChipContainer?.clientWidth ?? 0) -
        OVERFLOW_CHIP_WIDTH;
      const keywordChipContainerChildren =
        this.$refs.keywordChipContainer?.children ?? [];
      let totalWidth = 0;
      let i = 0;
      for (i = 0; i < keywordChipContainerChildren.length; i += 1) {
        totalWidth +=
          keywordChipContainerChildren[i].clientWidth + KEYWORD_CHIP_GAP_WIDTH;
        if (totalWidth > keywordChipContainerWidth) {
          break;
        }
      }
      return i;
    },

    getEducationDegreeFieldString(education) {
      if (!education) return '';
      const degreeName = education?.degree_name;
      const fieldOfStudy = education?.field_of_study;
      if (!degreeName && !fieldOfStudy) return '';
      if (!degreeName) return fieldOfStudy;
      if (!fieldOfStudy) return degreeName;
      return `${degreeName}, ${fieldOfStudy}`;
    },

    onShareExportClick(val) {
      this.$emit('shareExport', val);
    },

    onMoveClick(val) {
      this.$emit('move', val);
    },

    rejection_reason_formatted_string() {
      let final_formatted_string = '';
      if (this.candidate.rejection_reason_formatted) {
        let i;
        for (i = 0; i < this.candidate.rejection_reason_formatted.length; i++) {
          final_formatted_string +=
            this.candidate.rejection_reason_formatted[i];
          if (i === this.candidate.rejection_reason_formatted.length - 1) {
            break;
          } else {
            final_formatted_string += ', ';
          }
        }
      }

      return final_formatted_string;
    },

    onActivityHistoryClick() {
      this.$emit('activityHistory');
    },

    onChatClick() {
      this.$emit('chat');
    },

    onNoteClick() {
      this.$emit('note');
    },
  },
};
</script>
<template>
  <div
    v-if="candidate"
    class="tw-h-full tw-text-gray-900"
    :class="{ 'tw-opacity-50': disabled && grayOutStyle }"
  >
    <div :id="candidate._id" class="-tw-translate-y-[100px]"></div>
    <div
      class="tw-bg-white tw-rounded-lg tw-h-full tw-pb-[75px] tw-pt-4 tw-px-4 tw-border-2 tw-border-solid tw-transition-all tw-ease-in-out tw-relative"
      :class="{
        'tw-border-purple-600': isSelected,
        'tw-border-white': !isSelected,
        'tw-opacity-50 tw-relative': (isLoading && isSelected) || disabled,
        'hover:tw-shadow-shadow-2 tw-cursor-pointer':
          !candidate.bulk_sending_email,
      }"
    >
      <div
        v-if="isLoading && isSelected"
        class="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center"
      >
        <div class="tw-w-12 tw-h-12 tw-inline-block">
          <LoadingIcon class="tw-w-12" />
        </div>
      </div>
      <div class="tw-flex tw-gap-6 tw-items-center">
        <div>
          <div
            class="tw-w-[72px] tw-h-[72px] tw-rounded-full tw-overflow-hidden"
            @click="openCandidateCard"
          >
            <CustomImage
              :src="avatarUrl"
              class="tw-w-full tw-h-full tw-object-cover"
              :emptySrc="require('@/assets/img/empty_avatar.png')"
            />
          </div>
        </div>
        <div class="tw-w-full tw-min-w-0">
          <div
            class="tw-flex tw-items-start tw-justify-between tw-text-xl tw-leading-7 tw-font-semibold tw-font-poppins"
          >
            <div class="tw-pr-10" @click="openCandidateCard">
              {{ candidateName }}
            </div>
            <div
              class="tw-pt-1 select-candidate-checkbox tw-flex tw-gap-2 tw-items-center"
            >
              <div v-if="linkedinProfile" class="tw-cursor-pointer">
                <a :href="linkedinProfile" target="_blank">
                  <img :src="LinkedInIcon" />
                </a>
              </div>
              <Checkbox
                :value="isSelected"
                :disabled="disabled"
                :val="true"
                @input="onSelected"
              />
            </div>
          </div>

          <div
            v-if="candidate?.experiences?.length > 0"
            class="tw-truncate tw-mt-1"
            @click="openCandidateCard"
          >
            {{ candidate.headline || candidateCurrentTitle }}
          </div>
          <div
            class="tw-text-gray-700 tw-text-sm tw-mt-1 tw-leading-5"
            @click="openCandidateCard"
          >
            {{ candidateLocation }}
          </div>
        </div>
      </div>
      <div class="tw-pt-2" @click="openCandidateCard">
        <div
          v-if="candidate.experiences"
          class="tw-flex tw-items-start tw-gap-1 tw-mt-1"
        >
          <div class="tw-min-w-[92px] tw-font-semibold tw-text-sm">
            Experience
          </div>
          <div class="tw-truncate">
            <div
              v-for="(experience, i) in candidateExperiencesToDisplay"
              :key="`experience_${i}`"
              class="tw-font-normal tw-text-sm tw-truncate"
            >
              <div class="tw-text-ellipsis tw-overflow-hidden">
                {{ experience.title }} at {{ experience.company }}
              </div>
              <div
                class="tw-text-sm tw-text-gray-700 tw-mb-2 tw-text-ellipsis tw-overflow-hidden"
              >
                {{ experience.start_date }} -
                {{ !!experience.end_date ? experience.end_date : 'Present' }} ·
                {{ experience.tenure }}
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="candidate.education && candidate.education.length"
          class="tw-flex tw-gap-1 tw-items-start tw-mt-1"
        >
          <div class="tw-min-w-[92px] tw-font-semibold tw-text-sm">
            Education
          </div>
          <div class="tw-font-normal tw-text-sm tw-truncate">
            <div class="tw-text-ellipsis tw-overflow-hidden">
              {{ candidate.education[0].school }}
            </div>
            <div
              class="tw-text-sm tw-text-gray-700 tw-mb-2 tw-text-ellipsis tw-overflow-hidden"
            >
              {{ getEducationDegreeFieldString(candidate.education[0]) }}
            </div>
            <div
              v-if="!!candidate.education[0].starts_at"
              class="tw-text-sm tw-text-gray-700 tw-mb-2 tw-text-ellipsis tw-overflow-hidden"
            >
              {{
                !!candidate.education[0].starts_at
                  ? `${candidate.education[0].starts_at} -`
                  : ''
              }}
              {{
                !!candidate.education[0].ends_at
                  ? candidate.education[0].ends_at
                  : 'Present'
              }}
            </div>
          </div>
        </div>
        <div
          v-if="candidate?.matched_keywords?.length"
          ref="keywordContainer"
          class="tw-flex tw-gap-1 tw-items-start tw-mt-1"
        >
          <div class="tw-min-w-[92px] tw-font-semibold tw-text-sm tw-pt-1">
            Keywords
          </div>
          <div
            ref="keywordChipContainer"
            class="tw-flex tw-gap-2 tw-items-center tw-overflow-hidden"
          >
            <div
              v-for="(keyword, i) in sortedKeywords.slice(0, maxKeywords)"
              :key="`keyword_${i}`"
              class="tw-font-normal tw-text-sm"
            >
              <Chip small variant="outlined" :modelValue="keyword"></Chip>
            </div>
            <div v-if="hasKeywordOverflow">
              <Chip
                small
                variant="outlined"
                :modelValue="overflowKeywordsCount"
              ></Chip>
            </div>
          </div>
        </div>
      </div>
      <div
        class="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-pb-1 tw-px-4 tw-border-0 tw-border-t tw-border-gray-300 tw-border-solid"
      >
        <div class="tw-flex tw-items-center tw-justify-between tw-mt-1">
          <div
            class="tw-text-sm tw-text-gray-700 tw-leading-5 tw-flex tw-items-center tw-justify-between tw-py-1.5"
          >
            <div>
              <div
                v-if="tab === 'APPROVED'"
                class="card-footer-text text-0d875 ml-3"
              >
                <template>
                  <strong class="approved-title">Approved on: </strong>
                  {{ candidate.approved_date }}
                </template>
              </div>
              <div v-else-if="tab === 'CONTACTED'" class="text-0d875">
                <span class="reject-text">{{
                  detailed_contacted_status()
                }}</span>
              </div>
              <div v-else-if="tab === 'INTERVIEWING'" class="text-0d875">
                <span class="reject-title">Interview:</span>
                <span class="reject-text">{{ candidate.interview_date }}</span>
              </div>
              <div
                v-else-if="tab === 'REJECTED'"
                class="text-0d875 d-flex tw-items-center tw-gap-2"
              >
                <div class="reject-title pt-1">Rejected:</div>
                <v-tooltip
                  nudge-bottom="5px"
                  bottom
                  :disabled="showTooltip"
                  color="#F1F2F6"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div
                      class="reject-text"
                      style="padding-top: 3px"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ rejection_reason_formatted_string() }}
                    </div>
                  </template>
                  <span>
                    {{ rejection_reason_formatted_string() }}
                  </span>
                </v-tooltip>
                <div class="pl-1 open-menu-box" style="padding-top: 1px">
                  <v-menu
                    v-if="candidate.rejection_reason_other"
                    max-width="280px"
                    min-width="280px"
                    nudge-right="30px"
                    nudge-top="100px"
                    content-class="menu-list"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        width="23px"
                        height="23px"
                        v-bind="attrs"
                        style="z-index: 7"
                        class="!tw-static"
                        v-on="on"
                      >
                        <v-icon size="23">mdi-chevron-right</v-icon>
                      </v-btn>
                    </template>
                    <div class="tw-bg-white tw-rounded-lg tw-px-6 tw-py-3">
                      <div class="rejected-menu__title">
                        Rejected: {{ rejection_reason_formatted_string() }}
                      </div>
                      <div class="rejected-menu__text">
                        {{ candidate.rejection_reason_other }}
                      </div>
                    </div>
                  </v-menu>
                </div>
              </div>
              <div
                v-if="tab === 'NOT_INTERESTED'"
                class="tw-flex tw-gap-2 tw-items-center"
              >
                <div class="text-0d875">
                  <span class="reject-title">Not Interested:</span>
                  <span class="reject-text">{{ notInterestedReason }}</span>
                </div>
                <div
                  class="mr-3 position-relative"
                  @click="$emit('handleClick', $event)"
                >
                  <rejected-reason-box
                    :candidateId="candidate.id"
                    :tab="tab"
                    :projectId="projectId"
                    @submit="$emit('not-interested', $event)"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-if="!isHideMenu" class="tw-flex tw-items-center">
            <IconButton
              :disabled="disabled"
              class="tw-group tw-relative tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center"
              @click="onMailClick"
            >
              <img :src="MailIcon" />
              <div
                v-if="!disabled"
                class="-tw-mt-2 group-hover:tw-block tw-hidden tw-bg-gray-700 tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-white tw-absolute tw-bottom-full tw-left-0"
              >
                <Typography variant="body-2">Mail</Typography>
              </div>
            </IconButton>

            <div>
              <v-menu bottom offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <IconButton
                    :disabled="disabled"
                    class="tw-group tw-relative tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <img :src="SwitchIcon" />
                    <div
                      v-if="!disabled"
                      class="-tw-mt-2 group-hover:tw-block tw-hidden tw-bg-gray-700 tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-white tw-absolute tw-bottom-full tw-left-0"
                    >
                      <Typography variant="body-2">Move</Typography>
                    </div>
                  </IconButton>
                </template>
                <div class="tw-bg-white tw-rounded-lg">
                  <div
                    v-if="filteredTabs.length"
                    class="tw-max-h-[200px] tw-overflow-y-auto"
                  >
                    <div v-for="item in filteredTabs" :key="item.key">
                      <div
                        v-if="item.tab !== 'Interested'"
                        class="tw-py-2 tw-px-4 tw-cursor-pointer hover:tw-bg-purple-100"
                        @click="onMoveClick(item.key)"
                      >
                        <Typography variant="body-2">
                          {{ `Move to ${item.tab}` }}
                        </Typography>
                      </div>
                      <v-tooltip v-else top :disabled="!disabledMoveCandidate">
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on">
                            <div
                              class="tw-py-2 tw-px-4 tw-cursor-pointer hover:tw-bg-purple-100"
                              :disabled="disabledMoveCandidate"
                              @click="onMoveClick(item.key)"
                            >
                              <Typography variant="body-2">
                                {{ `Move to ${item.tab}` }}
                              </Typography>
                            </div>
                          </div>
                        </template>
                        <span
                          >Selected candidate(s) ineligible for
                          Interested.</span
                        >
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </v-menu>
            </div>
            <div>
              <v-menu bottom offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <IconButton
                    :disabled="disabled"
                    class="tw-group tw-relative tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <img :src="KebabIcon" />
                    <div
                      v-if="!disabled"
                      class="-tw-mt-2 group-hover:tw-block tw-hidden tw-bg-gray-700 tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-white tw-absolute tw-bottom-full tw-right-0 tw-whitespace-nowrap"
                    >
                      <Typography variant="body-2">More actions</Typography>
                    </div>
                  </IconButton>
                </template>
                <div class="tw-bg-white tw-rounded-lg">
                  <div
                    class="tw-py-2 tw-px-4 tw-cursor-pointer hover:tw-bg-purple-100"
                    @click="onActivityHistoryClick"
                  >
                    Activity history
                  </div>
                  <div
                    class="tw-py-2 tw-px-4 tw-cursor-pointer hover:tw-bg-purple-100"
                    @click="onChatClick"
                  >
                    Chat
                  </div>
                  <div
                    v-if="candidate.notes"
                    class="tw-py-2 tw-px-4 tw-cursor-pointer hover:tw-bg-purple-100"
                    @click="onNoteClick"
                  >
                    Note
                  </div>
                  <div
                    v-if="tab !== 'REJECTED' && tab !== 'NOT_INTERESTED'"
                    class="tw-py-2 tw-px-4 tw-cursor-pointer hover:tw-bg-purple-100"
                    @click="onShareExportClick(0)"
                  >
                    <Typography variant="body-2">Share Candidates</Typography>
                  </div>
                  <div
                    class="tw-py-2 tw-px-4 tw-cursor-pointer hover:tw-bg-purple-100"
                    @click="onShareExportClick(1)"
                  >
                    <Typography variant="body-2">Export as CSV</Typography>
                  </div>
                </div>
              </v-menu>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
